import React from 'react'
import styled, {css} from 'styled-components'
import {CalendarClock, Shield, type LucideIcon} from 'lucide-react'
import {Tooltip} from '../BaseComponents'

export type OrderHeaderFlagKind = 'backup' | 'extended'
export type OrderHeaderFlagSurface = 'card' | 'onBrand'

export interface OrderHeaderFlagProps {
  /** `backup`: the amber shield. `extended`: the blue calendar. */
  kind: OrderHeaderFlagKind
  /** Accessible name, e.g. "Open backup order JO-429" or "Extension details for JO-429". */
  label: string
  /** Bubble on hover, keyboard focus and tap. Falsy: no bubble. */
  tooltip?: React.ReactNode
  /** Click / Enter / Space. Unset: a plain icon, and a click on it reaches the card. */
  onActivate?: () => void
  /** The viewer can't open what the flag points at: the tooltip stays, aria-disabled, no action. */
  disabled?: boolean
  /** `onBrand` on the service order's blue header. Default `card`. */
  surface?: OrderHeaderFlagSurface
  'data-testid'?: string
}

const FLAG_TONES: Record<
  OrderHeaderFlagSurface,
  Record<OrderHeaderFlagKind, {bg: string; fg: string}>
> = {
  card: {
    backup: {bg: 'var(--warning-100)', fg: 'var(--warning-800)'},
    extended: {bg: 'var(--info-100)', fg: 'var(--info-800)'},
  },
  onBrand: {
    backup: {bg: 'var(--warning-500)', fg: 'var(--text-on-brand)'},
    extended: {bg: 'var(--info-100)', fg: 'var(--info-800)'},
  },
}

const ICONS: Record<OrderHeaderFlagKind, LucideIcon> = {backup: Shield, extended: CalendarClock}

type FlagStyleProps = {$kind: OrderHeaderFlagKind; $surface: OrderHeaderFlagSurface}

const flagCss = css<FlagStyleProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  width: var(--space-5);
  height: var(--space-5);
  padding: 0;
  border: 0;
  border-radius: var(--radius-pill);
  background: ${({$surface, $kind}) => FLAG_TONES[$surface][$kind].bg};
  color: ${({$surface, $kind}) => FLAG_TONES[$surface][$kind].fg};
`

const FlagIcon = styled.span<FlagStyleProps>`
  ${flagCss}
`

const FlagButton = styled.button<FlagStyleProps & {$enabled: boolean}>`
  ${flagCss}
  cursor: ${({$enabled}) => ($enabled ? 'pointer' : 'default')};

  ${({$enabled}) =>
    $enabled &&
    css`
      &:hover {
        filter: brightness(0.95);
      }
    `}

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

/**
 * A 20px flag beside an order card's id. It never opens the card: a click or Enter
 * runs `onActivate`, and its tooltip opens on hover, keyboard focus and tap (Escape closes it).
 */
export const OrderHeaderFlag: React.FC<OrderHeaderFlagProps> = ({
  kind,
  label,
  tooltip,
  onActivate,
  disabled = false,
  surface = 'card',
  'data-testid': testId,
}) => {
  const Icon = ICONS[kind]
  const icon = <Icon size={12} strokeWidth={2.25} aria-hidden="true" />

  if (!onActivate) {
    const plain = (
      <FlagIcon role="img" aria-label={label} $kind={kind} $surface={surface} data-testid={testId}>
        {icon}
      </FlagIcon>
    )
    return tooltip ? (
      <Tooltip content={tooltip} describeChild={false}>
        {plain}
      </Tooltip>
    ) : (
      plain
    )
  }

  const button = (
    <FlagButton
      type="button"
      $kind={kind}
      $surface={surface}
      $enabled={!disabled}
      aria-label={label}
      aria-disabled={disabled || undefined}
      data-testid={testId}
      onClick={(event) => {
        event.stopPropagation()
        if (!disabled) onActivate()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
      }}>
      {icon}
    </FlagButton>
  )
  return tooltip ? (
    <Tooltip content={tooltip} describeChild>
      {button}
    </Tooltip>
  ) : (
    button
  )
}

OrderHeaderFlag.displayName = 'OrderHeaderFlag'
